import React, { Suspense } from 'react';
import { StayZonesList } from '../StayZonesList';
import type { StayZoneAreaData } from '@shared/components/StayZoneTile/StayZoneTile.types';
import { StayZonesStatus } from '../StayZonesStatus';
import type { AutoPopulationPollingStatus } from '@shared/utils/autoPopulation/useAutoPopulation.types';
import { useStayZones } from '@shared/utils/stayZones/useStayZones';
import { calculateStayDates } from '@shared/utils/dateHelpers';
import { useEventInfo } from '@shared/utils/eventInfo/useEventInfo';
import { getVrboRedirectUrl } from '@shared/utils/stayZones/helpers';
import { withWindow } from '@shared/utils/withWindow';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useRemoveStayZone } from '../../hooks/useRemoveStayZone';
import { StayZoneRemoveDialog } from '../StayZoneRemoveDialog/StayZoneRemoveDialog';

interface StayZonesProps
  extends Readonly<{
    status: AutoPopulationPollingStatus;
  }> {}

const StayZonesContent: React.FC<StayZonesProps> = ({ status }) => {
  const { loading, stayZones } = useStayZones();
  const { eventInfo } = useEventInfo();
  const { checkIn, checkOut } = calculateStayDates(eventInfo?.finalizedDate?.dateString);

  const { stayZoneToDelete, deletedIds, handleRemove, handleCloseDialog, handleConfirmRemove } = useRemoveStayZone();

  const visibleStayZones = stayZones.filter(sz => !deletedIds.has(sz.id));

  const handleReorder = useEventCallback((_reorderedStayZones: ReadonlyArray<StayZoneAreaData>) => {
    // @todo [TRAV-2250] Implement API call to save new order
  });

  const handleView = useEventCallback((stayZone: StayZoneAreaData) => {
    const vrboUrl = getVrboRedirectUrl({
      destination: stayZone.address,
      location: stayZone.center,
      checkInDate: checkIn.dateString,
      checkOutDate: checkOut.dateString,
    });

    // @todo [TRAV-2322] Stay zone telemetry
    withWindow(global => global.open(vrboUrl, '_blank'));
  });

  return (
    <>
      <StayZonesStatus status={status} />
      <StayZonesList
        loading={loading}
        stayZones={visibleStayZones}
        onReorder={handleReorder}
        onView={handleView}
        onRemove={handleRemove}
      />
      <StayZoneRemoveDialog
        isOpen={stayZoneToDelete !== null}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmRemove}
      />
    </>
  );
};

export const StayZones: React.FC<StayZonesProps> = ({ status }) => {
  return (
    <Suspense fallback={null}>
      <StayZonesContent status={status} />
    </Suspense>
  );
};
